import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Button, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchIcon from '@material-ui/icons/Search';
import debounce from 'lodash.debounce';
import { joinTeam as joinTeamThunk } from 'redux/thunks/user';
import { getTeamNames } from 'api';
import { RootStore } from 'redux/store';
import { ModalWrapper } from 'components/Modals';
import './JoinTeamModal.scss';
import { useHistory } from 'react-router';

interface JoinTeamProps {
	open: boolean;
	hideBackdrop: boolean;
	allowClickOutsideToClose?: boolean;
	onBackClicked: () => void;
	onCloseClicked?: () => void;
	onJoinTeamClicked?: () => void;
}

interface Team {
	isPrivate: boolean;
	teamId: string;
	teamName: string;
	timeStamp: string;
}

const JoinTeam: React.FC<JoinTeamProps> = (props: JoinTeamProps) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const joiningTeam = useSelector((state: RootStore) => state.user.joiningTeam);
	const joinedNewTeam = useSelector((state: RootStore) => state.user.joinedNewTeam);
	const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(false);
	const [options, setOptions]: any[] = React.useState([]);
	const [searchString, setSearchString] = React.useState('');
	const [searching, setSearching] = React.useState(false);
	const [selectedTeam, setSelectedTeam] = React.useState<Team | null>(null);
	const performSearch = debounce(() => setAutoCompleteOpen(true), 300);
	const searchAgain = debounce(() => setSearching(true), 300);
	const loading = autoCompleteOpen && searching;
	const { open, hideBackdrop, allowClickOutsideToClose, onBackClicked, onJoinTeamClicked, onCloseClicked } = props;

	const handleOpen = () => {
		if (searchString.length > 0) {
			setSearching(true);
			performSearch();
		} else {
			setAutoCompleteOpen(false);
		}
	};

	const handleInputChange = (event: any, newInputValue: string) => {
		setSearchString(newInputValue);
		if (searchString.length > newInputValue.length && newInputValue.length > 0) {
			searchAgain();
		} else if (newInputValue.length === 0) {
			setAutoCompleteOpen(false);
		}
	};

	const selectTeam = (event: any, team: any) => {
		setSelectedTeam(team);
	};

	const joinSelectedTeam = (e: any) => {
		e.preventDefault();
		if (selectedTeam?.teamId) {
			dispatch(joinTeamThunk(selectedTeam?.teamId));
		}
	};

	useEffect(() => {
		if (joinedNewTeam && open) {
			history.push(`/team/${selectedTeam?.teamId}/all`);
			onJoinTeamClicked && onJoinTeamClicked();
		}
	}, [joinedNewTeam, open, onJoinTeamClicked, history, selectedTeam]);

	useEffect(() => {
		let active = true;
		if (!loading || searchString.length === 0) return undefined;

		(async () => {
			const teams: Team[] = await getTeamNames(searchString);
			if (active) {
				setOptions(teams);
				setSearching(false);
			}
		})();

		return () => {
			active = false;
		};
	}, [loading, searchString]);

	useEffect(() => {
		if (!autoCompleteOpen) {
			setOptions([]);
			setSearching(false);
		}
	}, [autoCompleteOpen]);

	return (
		<>
			<ModalWrapper open={open} hideBackdrop={hideBackdrop} onCloseClicked={onCloseClicked} allowClickOutsideToClose={allowClickOutsideToClose}>
				<form onSubmit={e => joinSelectedTeam(e)} className='join-team-form'>
					<Box className='join-team-box-layout'>
						<div className='join-team-header'>Join an existing team</div>

						<Grid item sm={8} className='join-team-grid'>
							<Autocomplete
								id='team-search'
								open={autoCompleteOpen}
								onOpen={handleOpen}
								onClose={() => {
									setAutoCompleteOpen(false);
								}}
								className='first-step'
								inputValue={searchString}
								onInputChange={handleInputChange}
								getOptionSelected={(option: Team, value: Team) => option.teamId === value.teamId}
								getOptionLabel={(option: Team) => option.teamName}
								options={options}
								loading={loading}
								onChange={selectTeam}
								getOptionDisabled={option => option.isPrivate}
								renderOption={option => (
									<React.Fragment>
										<div className='autocomplete-option'>
											<span>{option.teamName}</span>
											{option.isPrivate ? <span className='private-tag'>Private</span> : null}
										</div>
									</React.Fragment>
								)}
								renderInput={params => (
									<TextField
										{...params}
										className='join-team-search'
										fullWidth
										label='Search team'
										variant='outlined'
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<React.Fragment>
													{loading ? <CircularProgress color='inherit' size={20} /> : null}
													<SearchIcon className='join-team-search__icon' />
												</React.Fragment>
											),
										}}
									/>
								)}
							/>
						</Grid>
						<Grid className='form-action-buttons' container item justify='flex-end' alignItems='flex-end'>
							<Button color='primary' className='join-team-back-button' onClick={onBackClicked}>
								Back
							</Button>
							<Button type='submit' variant='contained' color='primary' disabled={joiningTeam}>
								Join Team
								{joiningTeam && <CircularProgress size={24} className='buttonProgress' />}
							</Button>
						</Grid>
					</Box>
				</form>
			</ModalWrapper>
		</>
	);
};

export default JoinTeam;
